import {
  SensorType,
  useSensorAlerts,
  useHumidityAlerts,
  HumidityAlertState,
  SensorAlertSeverity,
  SensorAlertType,
  AlertStatus,
} from 'common';
import React, { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppSync } from './AppSyncContext';

interface HumidityAlertContextType {
  alertState: HumidityAlertState;
  alertMessage: string;
  alertSeverity: SensorAlertSeverity | null;
  alertLocation: string;
  hasActiveHumidityAlerts: boolean;
  humidityAlertCount: number;
  onRequestService: () => Promise<void>;
  onClearAlert: () => Promise<void>;
  loading: boolean;
}

const HumidityAlertContext = createContext<HumidityAlertContextType | undefined>(undefined);

export const HumidityAlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const { sensors } = useAppSync();

  // Find humidity sensor (leak detector has humidity readings)
  const humiditySensor = sensors.find((sensor) => sensor.type === SensorType.LEAK_DETECTOR);

  const {
    sensorAlerts,
    fetchAndUpdateSensorAlerts,
    loading: alertsLoading,
  } = useSensorAlerts(humiditySensor?.id);

  // Use the common humidity alerts hook
  const {
    alertState,
    alertMessage,
    alertSeverity,
    location: alertLocation,
    activeAlert,
    loading,
    requestService,
    clearAlert,
  } = useHumidityAlerts({
    sensors,
    sensorAlerts: sensorAlerts || [],
    alertsLoading,
    translateFn: t,
    fetchAndUpdateSensorAlerts,
  });

  // Determine if there are active humidity alerts
  const hasActiveHumidityAlerts = alertState !== HumidityAlertState.NoAlert && !!activeAlert;

  // Count active humidity alerts (similar to mobile logic)
  const humidityAlertCount = React.useMemo(() => {
    if (!sensorAlerts || sensorAlerts.length === 0) return 0;

    const now = Date.now();
    const twentyFourHoursAgo = now - 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    return sensorAlerts.filter((alert) => {
      const isHumidityAlert = alert.alertType === SensorAlertType.HighHumidity;
      const isActiveStatus =
        alert.status === AlertStatus.NEW ||
        alert.status === AlertStatus.ONGOING ||
        alert.status === AlertStatus.PENDING ||
        alert.status === AlertStatus.ACTIVE;
      const isUnacknowledged = alert.acknowledgedAt === null && alert.acknowledgedBy === null;
      const isRecent = alert.timestamp && alert.timestamp * 1000 >= twentyFourHoursAgo;

      return isHumidityAlert && isActiveStatus && isUnacknowledged && isRecent;
    }).length;
  }, [sensorAlerts]);

  // Wrapper for request service to match context interface
  const handleRequestService = async () => {
    if (!humiditySensor) return;

    await requestService({
      hubId: humiditySensor.hubId,
      sensorId: humiditySensor.id,
      alertId: activeAlert?.id,
      notes: `Humidity alert: ${alertMessage}`,
    });
  };

  // Wrapper for clear alert to match context interface
  const handleClearAlert = async () => {
    await clearAlert();
  };

  return (
    <HumidityAlertContext.Provider
      value={{
        alertState,
        alertMessage,
        alertSeverity,
        alertLocation,
        hasActiveHumidityAlerts,
        humidityAlertCount,
        onRequestService: handleRequestService,
        onClearAlert: handleClearAlert,
        loading,
      }}>
      {children}
    </HumidityAlertContext.Provider>
  );
};

export const useHumidityAlertContext = (): HumidityAlertContextType => {
  const context = useContext(HumidityAlertContext);
  if (!context) {
    throw new Error('useHumidityAlertContext must be used within a HumidityAlertProvider');
  }
  return context;
};

export { HumidityAlertState };
